import { useState, useEffect, useRef, createContext, useContext, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { setSentryUser, clearSentryUser, captureSessionLost, captureRolelessSignout, captureAnonRead, type SessionLostReason } from "@/lib/sentry";
import { retryOnQueryError } from "@/lib/retryQuery";
import {
  getEffectiveCompanyId,
  readNestRefreshToken,
  readPrevNestRefreshToken,
  persistNestRefreshToken,
  clearNestRefreshToken,
  persistNestAccessToken,
  readNestAccessToken,
  clearNestAccessToken,
  resolveActiveRole,
  resolveActiveRoleForStaffSurface,
} from "@/lib/authHelpers";
import { nestSignOut, refreshWithRetry, NestRefreshError } from "@/services/api";
import { markAuthTransition } from "@/lib/nestFallback";
import { companiesRepository, profilesRepository } from "@/repositories";
import type { UserRoleAssignment } from "@/lib/rbac";

type ProfileRow = Database["public"]["Tables"]["profiles"]["Row"];

interface UserRole {
  role: "super_admin" | "admin" | "employee" | "owner" | "tenant" | "service_provider";
  company_id: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: ProfileRow | null;
  roles: UserRole[];
  companyId: string | null;
  isLoading: boolean;
  /** True while a Nest token refresh is in flight (focus/scheduled). Route
   *  guards wait on this so a focused/stale tab isn't bounced to /no-access or
   *  /auth before the freshly-rotated token lands. */
  isRefreshing: boolean;
  isAdmin: boolean;
  activeRole: string | null;
  setActiveRole: (role: string) => void;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  roleAssignments: UserRoleAssignment[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Classify a refresh failure for the Sentry signal: 'network' (transport / API
// unreachable), '5xx' (server restarting), a 4xx status string (token actually
// invalid), or 'unknown'. Used only for observability — the retry decision lives
// in refreshWithRetry(). Exported for unit testing of the classification branches.
// eslint-disable-next-line react-refresh/only-export-components -- pre-existing helper export in this hook file; extracting it is out of scope for this observability change.
export function refreshFailureReason(
  e: unknown,
): { reason: SessionLostReason; status?: number; outcome?: string } {
  if (e instanceof NestRefreshError) {
    const status = e.status;
    // Use a template literal (`${status}`) not String(status): the former keeps
    // the type as `\`${number}\``, so the union stays `'network' | '5xx' | \`${number}\``
    // instead of widening to a bare `string` (which loses the literal-tag contract).
    const reason: SessionLostReason =
      status === 0 ? "network" : status >= 500 ? "5xx" : `${status}`;
    // Translate the backend auth code into a human outcome so the lost-session
    // signal distinguishes a theft-revoke (reuse_detected) from a benign
    // unknown/expired token — the difference between "fix working as designed"
    // and "fix over-revoking". Undefined code (network/5xx) → no outcome.
    const outcome =
      e.code === "auth-009" ? "reuse_detected"
      : e.code === "auth-006" ? "invalid_or_expired"
      : e.code;
    return { reason, status, outcome };
  }
  return { reason: "unknown" };
}

/**
 * Returns true when the Nest access token is near expiry (within 60s) or
 * already expired, meaning a refresh should be attempted immediately.
 *
 * Pure function (no side-effects) — exported so it can be unit-tested in
 * isolation without mocking the full auth context. Mirrors the 60s REFRESH_SKEW_MS
 * used by the scheduled timer so both paths agree on what "near expiry" means.
 *
 * @param expiresAt - UNIX timestamp (seconds) from `session.expires_at`
 */
export function shouldRefreshNow(expiresAt: number): boolean {
  const REFRESH_SKEW_MS = 60_000;
  return Date.now() >= expiresAt * 1000 - REFRESH_SKEW_MS;
}

// Delay before re-attempting a Nest refresh that failed while the current
// access token is STILL valid (we refresh ~60s early, so a failure here is not
// fatal — the session is alive). Retried once-at-a-time until it succeeds or
// the token actually expires; see runNestRefresh catch.
const REFRESH_RETRY_WHILE_VALID_MS = 5_000;

/** Hard ceiling on the `isRefreshing` grace: route guards spin at most this long
 *  waiting for an in-flight refresh before evaluating auth normally again, so a
 *  hung refresh can never spin the UI forever. Set to 10s (was 5s): on a slow or
 *  contended network a refresh + roles re-read can take longer than 5s, and the
 *  old ceiling fired mid-flight → guards evaluated with activeRole=null → the
 *  user was bounced to /no-access (and a manual reload "fixed it"). We'd rather
 *  spin a few extra seconds than dead-end a valid session. */
const AUTH_REFRESH_GRACE_MS = 10_000;

/**
 * True when `expiresAt` (UNIX seconds) is in the future — i.e. the access token
 * has NOT actually expired yet. A proactive refresh that fails while this holds
 * must NOT force a logout: the session is still usable and the failure is
 * almost always a transient/contested rotation that recovers on retry. Pure +
 * exported for unit testing.
 */
// eslint-disable-next-line react-refresh/only-export-components -- pure helper exported for unit testing; mirrors the existing shouldRefreshNow export in this hook file.
export function accessTokenStillValid(expiresAt: number | undefined): boolean {
  return typeof expiresAt === "number" && Date.now() < expiresAt * 1000;
}

/**
 * True when a refresh failure is TRANSIENT — a transport failure (status 0:
 * offline, DNS, connection refused) or a 5xx (API restarting mid-deploy).
 * Neither proves the refresh token invalid, so the session must be kept and
 * the refresh retried; only a definitive 4xx verdict may force a signout.
 * Pure + exported for unit testing.
 */
// eslint-disable-next-line react-refresh/only-export-components -- pure helper exported for unit testing; mirrors the existing accessTokenStillValid export in this hook file.
export function isTransientRefreshError(e: unknown): boolean {
  return e instanceof NestRefreshError && (e.status === 0 || e.status >= 500);
}

const currentRoute = () =>
  typeof window !== "undefined" ? window.location.pathname : "unknown";

// Key for impersonation storage
const IMPERSONATION_STORAGE_KEY = "impersonation_session";

// Key for persisting the user's active role across navigation/remounts
export const ACTIVE_ROLE_KEY = "dallali-active-role";

// Privilege ranking for picking a multi-role user's DEFAULT active role on login.
// Higher = more privileged. A user who is both (say) admin and tenant should land
// on the admin context, not tenant — otherwise staff dashboards render empty and
// they must manually switch role every login (issue #1663).
const ROLE_PRIORITY: Record<string, number> = {
  super_admin: 6,
  admin: 5,
  manager: 4,
  employee: 3,
  owner: 2,
  tenant: 1,
};

/** Pick the highest-privilege role from a user's roles (unknown roles rank lowest). */
export function highestPrivilegeRole<T extends { role: string }>(roles: T[]): T | undefined {
  return roles.reduce<T | undefined>((best, r) => {
    if (!best) return r;
    return (ROLE_PRIORITY[r.role] ?? 0) > (ROLE_PRIORITY[best.role] ?? 0) ? r : best;
  }, undefined);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, _setSession] = useState<Session | null>(null);
  const [profile, _setProfile] = useState<ProfileRow | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [roleAssignments, setRoleAssignments] = useState<UserRoleAssignment[]>([]);
  const [companyId, _setCompanyId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // True while a Nest refresh is in flight — route guards spin on this instead
  // of bouncing a focused/stale tab to /no-access or /auth mid-refresh. Hard-
  // capped (AUTH_REFRESH_GRACE_MS) so a hung refresh can never spin forever.
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeRole, setActiveRoleState] = useState<string | null>(null);

  // Refs so the onAuthStateChange callback (empty-deps effect) sees current values.
  // Updated synchronously alongside state to avoid stale reads in async callbacks.
  const profileRef = useRef<ProfileRow | null>(null);
  const companyIdRef = useRef<string | null>(null);
  // Mirrors `session` so the refresh-failure handler (runNestRefresh, a
  // component-scope closure) can read the LIVE access-token expiry without the
  // effect's captured `session`. Used to avoid logging the user out when the
  // current access token is still valid (the spurious tab-return logout).
  const sessionRef = useRef<Session | null>(null);

  const setSession = (s: Session | null) => {
    sessionRef.current = s;
    _setSession(s);
  };

  const setProfile = (p: ProfileRow | null) => {
    profileRef.current = p;
    _setProfile(p);
  };
  const setCompanyId = (c: string | null) => {
    companyIdRef.current = c;
    _setCompanyId(c);
  };

  const setActiveRole = (role: string) => {
    setActiveRoleState(role);
    sessionStorage.setItem(ACTIVE_ROLE_KEY, role);
    // Switch company context to match the selected role
    const roleEntry = roles.find(r => r.role === role);
    if (roleEntry) {
      setCompanyId(getEffectiveCompanyId(roleEntry.company_id));
    }
  };

  // getEffectiveCompanyId imported from @/lib/authHelpers

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Healthy rotations are excluded — rationale on markAuthTransition.
        if (event !== 'TOKEN_REFRESHED' || !session) markAuthTransition();

        // Handle auth errors gracefully (e.g., invalid refresh token)
        if (event === 'TOKEN_REFRESHED' && !session) {
          // Token refresh failed → sign out. Previously SILENT (no signal) — a
          // blind spot in the forced-logout telemetry. Report it (deduped) so a
          // logout via this path is visible, not just the refresh-failure path.
          captureSessionLost({ reason: 'no_session', where: 'token-refreshed-no-session', route: currentRoute() });
          // Drop the cached Nest tokens too — consistent with the other signout
          // paths. They are stale once a refresh has failed, so a later recovery
          // attempt must not retry them.
          clearNestRefreshToken();
          clearNestAccessToken();
          // Involuntary — clear THIS client only; don't revoke the user's other devices.
          await nestSignOut({ scope: 'local' });
          setSession(null);
          setUser(null);
          setProfile(null);
          setRoles([]);
          setRoleAssignments([]);
          setCompanyId(null);
          setActiveRoleState(null);
          setIsLoading(false);
          clearSentryUser();
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);

        // Independently cache the Nest access token on every session-bearing
        // event (SIGNED_IN, TOKEN_REFRESHED, INITIAL_SESSION). This is the single
        // chokepoint every login + refresh passes through (each ends in
        // setSession()), so recoverNestSession() can forward the access token to
        // the reissue backstop even after a failed GoTrue refresh wipes the
        // stored Supabase session on a later reload (DALLALI-FRONTEND-AQ).
        if (session?.access_token) {
          persistNestAccessToken(session.access_token);
        }

        // Successful token refresh — session is still valid with a new JWT.
        // Skip setIsLoading(true) + fetchUserData to prevent ProtectedRoute from
        // unmounting children (which destroys open dialogs and form state).
        if (event === 'TOKEN_REFRESHED') {
          return;
        }

        // GoTrue re-emits SIGNED_IN on every tab-switch recovery (visibilitychange).
        // Skip refetch ONLY when the session is the same user we already have
        // loaded — otherwise we'd keep showing the prior user's profile/roles
        // after a fresh sign-in (e.g. verifyOtp during admin-invite accept
        // swaps the session from super_admin → new invitee).
        if (
          event === 'SIGNED_IN' &&
          session?.user &&
          profileRef.current &&
          companyIdRef.current &&
          session.user.id === profileRef.current.id
        ) {
          return;
        }

        // User identity changed → fully reset the previous user's state so
        // the header (and anything else reading useAuth) doesn't render the
        // OLD identity while fetchUserData is in flight. Also clears the
        // stored active-role preference so a stale "super_admin" doesn't
        // linger when the new user has no roles yet (roles are populated
        // later, e.g. by the admin-invite activation RPC).
        if (
          event === 'SIGNED_IN' &&
          session?.user &&
          profileRef.current &&
          session.user.id !== profileRef.current.id
        ) {
          sessionStorage.removeItem(ACTIVE_ROLE_KEY);
          setProfile(null);
          setRoles([]);
          setRoleAssignments([]);
          setCompanyId(null);
          setActiveRoleState(null);
        }

        // Defer fetching profile data (only for genuine first sign-in)
        if (session?.user) {
          setIsLoading(true);
          setTimeout(() => {
            fetchUserData(session.user.id);
          }, 0);
        } else {
          setProfile(null);
          setRoles([]);
          setRoleAssignments([]);
          setCompanyId(null);
          setActiveRoleState(null);
          setIsLoading(false);
        }
      }
    );

    // Recover a Nest-minted session from the independently-cached refresh token.
    // Returns true if setSession() succeeded — which fires onAuthStateChange
    // (SIGNED_IN) and drives fetchUserData, so the caller should stop.
    const recoverNestSession = async (): Promise<boolean> => {
      const cached = readNestRefreshToken();
      if (!cached) return false;
      try {
        // Forward the locally-cached access token (if any) so a legacy session
        // whose refresh token has no nest_sessions row can be reissued server-side
        // instead of being dropped on recovery (DALLALI-FRONTEND-AQ). Prefer the
        // Supabase client's stored session, but fall back to our independent copy:
        // this very recovery path runs AFTER getSession()'s own GoTrue refresh has
        // failed and WIPED the stored session, so cachedSession is usually null
        // here — without the fallback the access token is gone and the reissue
        // backstop silently no-ops. No GoTrue round-trip either way.
        const { data: { session: cachedSession } } = await supabase.auth.getSession();
        const accessToken = cachedSession?.access_token ?? readNestAccessToken();
        const tokens = await refreshWithRetry(
          cached,
          readPrevNestRefreshToken(),
          accessToken,
        );
        const { data, error } = await supabase.auth.setSession({
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token,
        });
        if (error || !data.session) {
          throw error ?? new Error("setSession returned no session");
        }
        persistNestRefreshToken(tokens.refresh_token);
        return true;
      } catch (e) {
        console.warn("[Auth] Nest session recovery failed:", e);
        const { reason, status, outcome } = refreshFailureReason(e);
        captureSessionLost({ reason, where: "recover", status, outcome, route: currentRoute() });
        clearNestRefreshToken();
        clearNestAccessToken();
        // Also wipe the stale Supabase session from localStorage. Without this,
        // a definitively-dead refresh token lingers in storage and every reload
        // retries it → "Refresh token is not valid" loop that only an incognito
        // window escapes. Clearing it here drops the user to a clean login.
        // (nestSignOut local-scope = supabase.auth.signOut({scope:'local'}), no
        // network revoke of the user's other devices.)
        await nestSignOut({ scope: 'local' });
        return false;
      }
    };

    // THEN check for existing session
    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      if (error) {
        // For JWT clock skew errors ("JWT issued at future"), rely on auto-refresh
        // instead of immediately signing out. The auth state listener will handle
        // session changes if the refresh succeeds or fails.
        if (error.message.includes('JWT issued at future')) {
          console.warn('JWT clock skew detected, relying on auto-refresh:', error.message);
          setIsLoading(false);
          return;
        }

        // A stored-session error can be a near-expiry GoTrue refresh failure
        // left behind in localStorage (GoTrue cannot rotate a Nest token).
        // Recover from the cached Nest refresh token before signing out.
        if (await recoverNestSession()) return;

        // For other session errors, sign out. recoverNestSession() above already
        // reported if it THREW; this covers the silent blind spot where it bailed
        // (e.g. no cached Nest token) and we sign out with no signal. Deduped, so
        // it won't double-count when recover already reported.
        console.warn('Session retrieval failed:', error.message);
        captureSessionLost({
          reason: 'no_session',
          where: 'init-session-error',
          status: (error as { status?: number }).status,
          route: currentRoute(),
        });
        // Involuntary (session-init error) — local-only.
        clearNestRefreshToken();
        clearNestAccessToken();
        nestSignOut({ scope: 'local' });
        setIsLoading(false);
        clearSentryUser();
        return;
      }

      // No stored session → attempt recovery from the cached token (the
      // Supabase session may have been wiped by a failed GoTrue refresh).
      if (!session?.user) {
        setIsLoading(true);
        if (await recoverNestSession()) return;
        setIsLoading(false);
      }

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        setIsLoading(true);
        fetchUserData(session.user.id);
      } else {
        setCompanyId(null);
        setIsLoading(false);
        clearSentryUser();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Listen for impersonation changes (both storage events and custom events)
  useEffect(() => {
    const handleImpersonationChange = () => {
      // Re-check impersonation state and update companyId immediately
      const stored = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
      if (stored) {
        try {
          const session = JSON.parse(stored) as {
            sessionId: string;
            target: { companyId: string };
          };
          if (session.target?.companyId) {
            setCompanyId(session.target.companyId);
            return;
          }
        } catch {
          // Ignore parse errors
        }
      }
      // If no impersonation, restore original company ID from profile
      if (profile?.company_id) {
        setCompanyId(profile.company_id);
      }
    };

    // Listen for custom impersonation-changed event (fired when impersonation starts/ends)
    window.addEventListener("impersonation-changed", handleImpersonationChange);
    // Also listen for storage events (for cross-tab sync)
    window.addEventListener("storage", handleImpersonationChange);
    
    return () => {
      window.removeEventListener("impersonation-changed", handleImpersonationChange);
      window.removeEventListener("storage", handleImpersonationChange);
    };
  }, [profile?.company_id]);

  // In-flight guard: holds the pending runNestRefresh promise so a concurrent
  // focus/visibility event and a scheduled timer cannot both fire a refresh
  // simultaneously. Whichever arrives second awaits the first and then no-ops
  // (because setSession will have already updated `session`, re-running the
  // scheduled effect to arm the next timer).
  const refreshInFlightRef = useRef<Promise<void> | null>(null);

  // Holds the timer id for a single pending "retry-while-still-valid" attempt
  // (see runNestRefresh catch). Ensures at most ONE outstanding retry so a
  // failing refresh against a still-valid access token doesn't stack timers.
  const refreshRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // D2 (nest mode): refresh the Nest-minted session shortly before it expires.
  // Supabase's own auto-refresh ticker is disabled in nest mode (client.ts)
  // because GoTrue cannot rotate a Nest-issued refresh token; we rotate via the
  // Nest POST /auth/refresh endpoint and re-inject with setSession() so Storage /
  // Realtime / PostgREST always hold a live JWT. No-op under the gotrue provider.
  //
  // runNestRefresh() is the single shared implementation called by both the
  // scheduled timer below AND the focus/visibility handler further below.
  const runNestRefresh = (where: string, activeGuard: { current: boolean }) =>
    async (refreshToken: string): Promise<void> => {
      // Open the grace window so route guards spin instead of bouncing a focused/
      // stale tab to /no-access or /auth while the new token is in flight. Hard-
      // capped so a hung refresh can't spin the guards forever.
      setIsRefreshing(true);
      const graceCeiling = setTimeout(() => setIsRefreshing(false), AUTH_REFRESH_GRACE_MS);
      // Forward the current access token so the server can reissue a session when
      // a legacy refresh token has no nest_sessions row (DALLALI-FRONTEND-AQ).
      const currentAccessToken = sessionRef.current?.access_token;
      try {
        const tokens = await refreshWithRetry(
          refreshToken,
          readPrevNestRefreshToken(),
          currentAccessToken,
        );
        if (!activeGuard.current) return;
        persistNestRefreshToken(tokens.refresh_token);
        // setSession emits SIGNED_IN (same user → fetchUserData short-circuits in
        // the listener) and updates `session`, which re-runs this effect to
        // schedule the next refresh cycle.
        await supabase.auth.setSession({
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token,
        });
      } catch (e) {
        if (!activeGuard.current) return;

        // (1) CONCURRENT ROTATION — another refresh cycle in this tab, or another
        // tab sharing localStorage, may have already rotated the token while ours
        // was in flight. If the stored Nest token changed since we presented
        // `refreshToken`, the session is still alive: retry ONCE with the fresh
        // token instead of signing out. This is the desync behind the spurious
        // tab-return logouts (Sentry: where=scheduled-refresh|focus-refresh,
        // reason=401, outcome=invalid_or_expired|reuse_detected).
        const latest = readNestRefreshToken();
        if (latest && latest !== refreshToken) {
          try {
            const tokens = await refreshWithRetry(
              latest,
              readPrevNestRefreshToken(),
              currentAccessToken,
            );
            if (!activeGuard.current) return;
            persistNestRefreshToken(tokens.refresh_token);
            await supabase.auth.setSession({
              access_token: tokens.access_token,
              refresh_token: tokens.refresh_token,
            });
            return; // recovered — no logout
          } catch {
            // Fresh token also failed → fall through to the checks below.
          }
        }

        // (2) SESSION NOT PROVEN DEAD — keep it and retry once-at-a-time.
        // Two independent reasons a failure here does not mean the session is
        // gone:
        //  - We refresh ~60s BEFORE expiry, so while the access token is still
        //    valid the session remains fully usable.
        //  - A TRANSIENT failure (status 0 network / 5xx restart) says nothing
        //    about the refresh token's validity — laptop wake before Wi-Fi
        //    reconnects, mid-deploy API restart. Signing out here was the
        //    dominant forced-signout in production (DALLALI-FRONTEND-AQ:
        //    reason=network, status=0, where=scheduled-refresh) — the user is
        //    offline anyway, and once the network returns the SAME refresh
        //    token succeeds. Only a definitive server verdict (4xx) may end
        //    the session.
        if (
          accessTokenStillValid(sessionRef.current?.expires_at) ||
          isTransientRefreshError(e)
        ) {
          if (!refreshRetryTimerRef.current) {
            refreshRetryTimerRef.current = setTimeout(() => {
              refreshRetryTimerRef.current = null;
              if (!activeGuard.current) return;
              if (refreshInFlightRef.current) return;
              const tok = readNestRefreshToken() ?? sessionRef.current?.refresh_token;
              if (!tok) return;
              refreshInFlightRef.current = runNestRefresh(where, activeGuard)(tok);
            }, REFRESH_RETRY_WHILE_VALID_MS);
          }
          return; // session still usable — do not clear token or sign out
        }

        // (3) GENUINE LOSS — the server definitively rejected the refresh
        // token (4xx) AND the access token has expired. Clear the cached token
        // and sign out; the auth listener resets state on SIGNED_OUT.
        console.warn("[Auth] Nest token refresh failed, signing out:", e);
        const { reason, status, outcome } = refreshFailureReason(e);
        captureSessionLost({ reason, where, status, outcome, route: currentRoute() });
        clearNestRefreshToken();
        clearNestAccessToken();
        // Involuntary (refresh failed) — local-only; other devices keep their sessions.
        await nestSignOut({ scope: 'local' });
      } finally {
        clearTimeout(graceCeiling);
        setIsRefreshing(false);
        refreshInFlightRef.current = null;
      }
    };

  useEffect(() => {
    if (!session?.expires_at) return;

    const REFRESH_SKEW_MS = 60_000; // refresh 60s before expiry
    const msUntilRefresh = Math.max(
      session.expires_at * 1000 - Date.now() - REFRESH_SKEW_MS,
      0,
    );

    // Guard against an in-flight /auth/refresh resolving after this effect is
    // torn down (unmount or session change) — avoids a stray setSession/signOut
    // on a stale cycle.
    const activeGuard = { current: true };
    const timer = setTimeout(() => {
      const refreshToken = readNestRefreshToken() ?? session.refresh_token;
      if (refreshInFlightRef.current) {
        // Another refresh is already in flight (e.g. a focus event raced us).
        // Await it silently — when it resolves/rejects, `session` will have
        // changed and this effect will re-run to arm the next timer.
        return;
      }
      const p = runNestRefresh("scheduled-refresh", activeGuard)(refreshToken);
      refreshInFlightRef.current = p;
    }, msUntilRefresh);

    return () => {
      activeGuard.current = false;
      clearTimeout(timer);
      // A session change means a refresh succeeded (or the user signed out) —
      // cancel any pending "retry-while-still-valid" attempt; it's now moot.
      if (refreshRetryTimerRef.current) {
        clearTimeout(refreshRetryTimerRef.current);
        refreshRetryTimerRef.current = null;
      }
    };
  }, [session]);

  // Focus/visibility refresh: when the tab regains focus after being
  // backgrounded, proactively refresh the Nest session IF the access token
  // is near or past expiry — instead of relying solely on the Safari-throttled
  // background setTimeout, which can fire *after* the 60s server-side rotation
  // grace window and produce a genuine 401 that cannot be retried.
  // Only fires when a session exists and the token is within 60s of expiry (or
  // already expired) to avoid hammering the refresh endpoint on every tab-switch.
  useEffect(() => {
    const activeGuard = { current: true };

    const attemptRefreshIfDue = (where: string) => {
      if (!session?.expires_at) return;
      if (!shouldRefreshNow(session.expires_at)) return;

      if (refreshInFlightRef.current) {
        // Scheduled timer already kicked off a refresh — await it, no-op here.
        return;
      }
      const refreshToken = readNestRefreshToken() ?? session.refresh_token;
      const p = runNestRefresh(where, activeGuard)(refreshToken);
      refreshInFlightRef.current = p;
    };

    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      attemptRefreshIfDue("focus-refresh");
    };

    // Reconnect after a transient network loss (laptop wake, tunnel, flaky
    // mobile data): retry the refresh the moment the browser reports the
    // network is back instead of waiting for the next 5s retry tick — pairs
    // with the no-signout-on-transient-failure rule in runNestRefresh.
    // Deliberately NO visibility guard: the dominant laptop-wake case restores
    // connectivity while the tab is still hidden, where retry timers are
    // browser-throttled — a hidden tab must still refresh on reconnect.
    const handleOnline = () => attemptRefreshIfDue("online-refresh");

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleVisibility);
    window.addEventListener("online", handleOnline);

    return () => {
      activeGuard.current = false;
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleVisibility);
      window.removeEventListener("online", handleOnline);
      // Also cancel any pending "retry-while-still-valid" attempt on unmount /
      // session change so no stray timer survives this effect (belt-and-braces
      // with the scheduled-refresh effect's cleanup).
      if (refreshRetryTimerRef.current) {
        clearTimeout(refreshRetryTimerRef.current);
        refreshRetryTimerRef.current = null;
      }
    };
  }, [session]);

  const fetchUserData = async (userId: string, healAttempted = false) => {
    setIsLoading(true);

    try {
      // Fetch profile — session-boot read (D3-owned auth path): direct
      // Supabase transport via the repository; a read error is non-fatal
      // here (roles below decide access), so it resolves to null.
      const profileData = await profilesRepository
        .getByIdDirect(userId)
        .catch(() => null);

      if (profileData) {
        setProfile(profileData);
      }

      // Fetch roles — retry a transient read error before giving up. A failed
      // first read right after login (momentarily-expired JWT → 401, network
      // blip) would otherwise leave `activeRole` unresolved, so the /no-access
      // self-heal (which waits for activeRole) never fires and the user is
      // stranded there until an external auth event — the "stuck on /no-access,
      // only a refresh fixes it" report. Retrying recovers the read in place.
      const { data: rolesData, error: rolesError } = await retryOnQueryError(
        () =>
          supabase
            .from("user_roles")
            .select("role, company_id")
            .eq("user_id", userId),
        {
          // Login-critical path: cap the retry so a transient blip recovers
          // without a long stall — 4 attempts × 600ms ≈ 1.8s worst case (kept
          // explicit so the cost on this path is visible). Widened from 3×400ms:
          // a slow first roles read on a contended network was exhausting the
          // retries while still empty, leaving activeRole null → /no-access.
          attempts: 4,
          delayMs: 600,
          // Retry an ERROR *or* an EMPTY-but-error-free read. The latter is the
          // real bug behind "logged out / account not set up, then a refresh
          // brings me back": right after setSession the RLS context can briefly
          // resolve to zero rows for a user who DOES have roles, which trips the
          // genuine-roleless signout below. Retrying turns that transient empty
          // into the user's real roles. A user who is genuinely roleless still
          // reads empty on every attempt and is correctly signed out after.
          //
          // EXCEPTION: during active owner onboarding an empty read is EXPECTED
          // (the role hasn't been granted yet) and the guard below deliberately
          // skips the signout. Retrying there only adds ~0.8s to the signup
          // critical path for a result we know won't change — so retry on error
          // only while onboarding is in flight.
          shouldRetry: (r) => {
            if (r.error) return true;
            const empty = Array.isArray(r.data) && r.data.length === 0;
            if (!empty) return false;
            return sessionStorage.getItem("dallali-onboarding-active") !== "1";
          },
        },
      );

      // A roles query still failing after retries must NOT be misread as "user
      // has zero roles" — that misclassification force-logs-out a perfectly
      // valid session, which is exactly the tab-return login flicker. Bail
      // gracefully and keep the session: the refresh loop restores a live token
      // and the next fetchUserData (fired by the ensuing SIGNED_IN) succeeds.
      // The genuine-roleless / deactivated-company guards below run ONLY when
      // the query actually succeeded.
      if (rolesError) {
        setIsLoading(false);
        return;
      }

      // Enforce company deactivation: if the super admin flipped a
      // company's is_active to false, users belonging to that company
      // should no longer have access. Filter out roles tied to inactive
      // companies — EXCEPT super_admin, so a super admin can always log
      // back in to reactivate a company (and so we never self-lockout the
      // platform operators).
      const companyIdsFromRoles = Array.from(
        new Set(
          (rolesData ?? [])
            .map((r) => r.company_id)
            .filter((id): id is string => !!id),
        ),
      );
      let inactiveCompanyIds = new Set<string>();
      if (companyIdsFromRoles.length > 0) {
        // Boot-path activity read (Supabase-internal via the repository —
        // listActivity never throws; an error resolves to [] so a blip can't
        // lock users out).
        const companyActivity =
          await companiesRepository.listActivity(companyIdsFromRoles);
        inactiveCompanyIds = new Set(
          companyActivity
            .filter((c) => c.is_active === false)
            .map((c) => c.id),
        );
      }

      const activeRolesData = (rolesData ?? []).filter(
        (r) => r.role === "super_admin" || !inactiveCompanyIds.has(r.company_id),
      );

      // If every one of the user's affiliations is in a deactivated
      // company (and they're not a super_admin), sign them out with a
      // clear message. Otherwise they'd see a blank dashboard and wonder
      // what's broken.
      if ((rolesData?.length ?? 0) > 0 && activeRolesData.length === 0) {
        // Involuntary (deactivated-company guard) — local-only.
        clearNestRefreshToken();
        clearNestAccessToken();
        await nestSignOut({ scope: 'local' });
        setProfile(null);
        setRoles([]);
        setRoleAssignments([]);
        setCompanyId(null);
        setActiveRoleState(null);
        setIsLoading(false);
        // Defer the toast so the import cost stays with the module that
        // already uses it — useAuth currently doesn't import sonner.
        // Concat AR + EN: this callback runs outside the React tree
        // (onAuthStateChange), so useLanguage() isn't available.
        import("sonner").then(({ toast }) => {
          toast.error(
            "تم تعطيل شركتك من قِبل الإدارة — تواصل مع الدعم. " +
              "Your company has been deactivated by the admin — please contact support.",
          );
        });
        return;
      }

      // If the user has ABSOLUTELY ZERO user_roles rows (not just all in
      // inactive companies — that case is handled above), sign them out
      // with a clear message. This prevents a roleless user from failing
      // open to "admin" and seeing the admin dashboard with no data.

      // Suppress during active onboarding (#1930 guard mid-signup fix):
      // During owner signup there's a brief window (setSession →
      // onAuthStateChange → fetchUserData) where the session is live but
      // the owner role hasn't propagated yet. The sessionStorage flag
      // "dallali-onboarding-active" is set by OwnerAuth.tsx at the start
      // of OTP verification and cleared on completion/error. Skip the
      // signOut+toast when the flag is "1" — the role will resolve
      // momentarily. Keep the genuine-roleless guard for non-onboarding
      // sessions (truly unprovisioned users still fail closed).
      if ((rolesData?.length ?? 0) === 0) {
        // Skip the guard during active onboarding (#1930): roles propagate
        // momentarily. (The empty if/else is collapsed to one guarded branch —
        // both the onboarding-skip and the guard fall through to the `return`.)
        if (sessionStorage.getItem('dallali-onboarding-active') !== '1') {
          // An empty user_roles read is INDISTINGUISHABLE from a read that ran
          // before the Nest session was applied to the supabase client: an
          // unauthenticated (anon) request resolves auth.uid() to null, so RLS
          // returns 0 rows — byte-identical to a genuinely roleless user. We
          // PROVED this in production (DALLALI-FRONTEND-BF): the SAME token + RLS
          // return the user's real roles the moment the session is applied. So
          // before the irreversible force-signout, confirm the client is
          // actually authenticated as THIS user. getSession() reflects the
          // client's live session; when it holds a token for `userId`, that
          // token was on the (retried) reads, so an empty result is genuinely
          // roleless. When it does NOT (anon — e.g. a racing local signOut from a
          // failed tab-return recovery cleared the just-applied session), the
          // empty read is meaningless: bail WITHOUT signing out and let the auth
          // flow resettle (the next SIGNED_IN re-runs fetchUserData with the
          // token applied). This is the actual fix for the "account not set up"
          // forced-logout; the retry above only helps once the session is on.
          // A getSession() error counts as "not confirmed authenticated" — a
          // client that can't read its own session must never force a logout, so
          // it takes the same bail path (fail safe under uncertainty).
          const { data: { session: liveSession }, error: sessionError } =
            await supabase.auth.getSession();
          const authedAsUser =
            !sessionError && !!liveSession?.access_token && liveSession.user?.id === userId;
          if (!authedAsUser) {
            // NOT a signout — record a distinct, benign signal (own message +
            // dedup key) so triage never hunts for a logout that didn't happen,
            // and so this can't collapse a genuine session-loss in the same window.
            captureAnonRead({ userId, route: currentRoute() });
            setIsLoading(false);
            return;
          }

          // Safety-net: a genuinely-authenticated user with zero roles is the
          // park-at-invite regression this release fixes — their role was never
          // written (a missed park, or an invite created before this fix). Before
          // the irreversible force-signout, self-heal ONCE from their pending
          // invitation / linked directory row, then re-read. Guarded by
          // healAttempted so a no-op heal can never loop; if the RPC is absent
          // (FE deployed ahead of the migration) it errors → healed falsy →
          // falls through to the original signout (safe degradation).
          if (!healAttempted) {
            const { data: healResult } = await supabase.rpc(
              "heal_parked_user" as never,
              { p_user_id: userId } as never,
            );
            if ((healResult as { healed?: boolean } | null)?.healed) {
              return fetchUserData(userId, true);
            }
          }

          // Make this otherwise-silent forced signout observable: the `release`
          // tag tells a stale-bundle hit apart from an empty read that survived
          // the retry on the current release.
          captureRolelessSignout({ userId, route: currentRoute() });
          // Involuntary (genuine-roleless guard) — local-only.
          clearNestRefreshToken();
          clearNestAccessToken();
          await nestSignOut({ scope: 'local' });
          setProfile(null);
          setRoles([]);
          setRoleAssignments([]);
          setCompanyId(null);
          setActiveRoleState(null);
          setIsLoading(false);
          import("sonner").then(({ toast }) => {
            toast.error(
              "حسابك غير مُهيأ بعد، تواصل مع الدعم. " +
                "Your account is not set up yet — contact support.",
            );
          });
        }
        return;
      }

      const typedRoles: UserRole[] = [];
      if (!rolesError && rolesData) {
        for (const row of activeRolesData) {
          typedRoles.push({
            role: row.role as UserRole["role"],
            company_id: row.company_id,
          });
        }
        setRoles(typedRoles);

        // Set activeRole. resolveActiveRoleForStaffSurface() is only safe on
        // the STAFF surface (/dashboard, /super-admin) — there it corrects a
        // multi-role user (e.g. admin + owner) whose sessionStorage still
        // carries `x-active-role: owner` from a prior portal visit, so RLS
        // policies requiring staff capabilities don't silently no-op. See:
        // fix/unit-deed-active-role-rls.
        //
        // On a PORTAL url (/portal/<role>/...) the same call wrongly upgrades
        // the stored portal role back to the highest staff role, then RoleGate
        // sees `activeRole !== "tenant"` and Navigate's the user to
        // /super-admin on every refresh. Decide which resolver to use based on
        // the current location, not blindly.
        if (typedRoles.length > 0) {
          const pathname =
            typeof window !== "undefined" ? window.location.pathname : "";
          const isStaffSurface =
            pathname.startsWith("/dashboard") ||
            pathname.startsWith("/super-admin");
          const resolvedRole = isStaffSurface
            ? resolveActiveRoleForStaffSurface(typedRoles)
            : resolveActiveRole(typedRoles);
          const initialRole = resolvedRole ?? (highestPrivilegeRole(typedRoles) ?? typedRoles[0]).role;
          setActiveRoleState(initialRole);
          sessionStorage.setItem(ACTIVE_ROLE_KEY, initialRole);
        }
      }

      // Resolve companyId from the active role's company (falls back to profile/first role)
      const activeEntry = (() => {
        const storedRole = sessionStorage.getItem(ACTIVE_ROLE_KEY);
        return storedRole
          ? typedRoles.find(r => r.role === storedRole)
          : null;
      })();
      const resolvedFromProfile = profileData?.company_id ?? null;
      const resolvedFromRoles = (highestPrivilegeRole(typedRoles) ?? typedRoles[0])?.company_id ?? null;
      let resolvedCompanyId = activeEntry?.company_id ?? resolvedFromProfile ?? resolvedFromRoles;

      if (!resolvedCompanyId) {
        const { data: rpcCompanyId, error: rpcError } = await supabase.rpc(
          "get_user_company_id",
          { user_uuid: userId }
        );
        if (!rpcError && rpcCompanyId) {
          resolvedCompanyId = rpcCompanyId as string;
        }
      }

      // Apply impersonation override if active
      const effectiveCompanyId = getEffectiveCompanyId(resolvedCompanyId);

      // Fetch role assignments from junction table (requires companyId to be resolved)
      // Cast: user_role_assignments table added in migration but not yet in generated types
      const companyForAssignments = effectiveCompanyId || resolvedCompanyId;
      if (companyForAssignments) {
        const { data: assignmentsData } = await supabase.from('user_role_assignments')
          .select('id, role_id, role_permissions!inner(role_name, portal_type, permissions, name_id)')
          .eq('user_id', userId)
          .eq('company_id', companyForAssignments);

        if (assignmentsData) {
          const mapped = assignmentsData.map(a => ({
            id: a.id,
            role_id: a.role_id,
            role_permissions: a.role_permissions as unknown as UserRoleAssignment['role_permissions'],
          }));
          setRoleAssignments(mapped);
        } else {
          setRoleAssignments([]);
        }
      } else {
        setRoleAssignments([]);
      }

      setCompanyId(effectiveCompanyId ?? null);

      // Set Sentry user context after successful auth
      if (profileData) {
        setSentryUser({
          id: userId,
          phone: profileData.phone ?? undefined,
          company_id: effectiveCompanyId ?? undefined,
        });
      }
    } catch (_error) {
      // Auth data fetch failed - user will see loading state cleared
    } finally {
      setIsLoading(false);
    }
  };

  const refreshProfile = async () => {
    if (!user) return;
    // Self read — rides the flipped repository path (GET /profiles/me,
    // Supabase fallback). A failed refresh keeps the current profile.
    const data = await profilesRepository.getById(user.id).catch(() => null);
    if (data) {
      setProfile(data);
    }
  };

  const signOut = async () => {
    // Clear impersonation, active role, and the cached Nest refresh + access
    // tokens on sign out. The brand color cache is kept so the next login
    // paints the company color immediately (useBrandColor resets the CSS
    // vars itself when the branded layout unmounts).
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    sessionStorage.removeItem(ACTIVE_ROLE_KEY);
    clearNestRefreshToken();
    clearNestAccessToken();

    await nestSignOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
    setRoleAssignments([]);
    setCompanyId(null);
    clearSentryUser();
  };

  const isAdmin = activeRole === "admin" || activeRole === "super_admin";

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        roles,
        companyId,
        isLoading,
        isRefreshing,
        isAdmin,
        activeRole,
        setActiveRole,
        signOut,
        refreshProfile,
        roleAssignments,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
